import { useEffect } from "react";
import type { ErrorKind, ViewerError } from "@/services/client";
import { notifyEmbedError } from "@/services/handshake";

// The shell's loading and failure UI. Every state is distinct, honest,
// and token-themed — never a blank frame or an infinite spinner. Deliberately
// unbranded: this surface renders inside embedding pages.

const COPY: Record<ErrorKind, { title: string; detail: string }> = {
  "unknown-type": {
    title: "This document can't be displayed",
    detail: "The link points to a document type that isn't supported.",
  },
  "invalid-id": {
    title: "This document can't be displayed",
    detail: "The link doesn't point to a valid document.",
  },
  unsupported: {
    title: "This document can't be displayed here yet",
    detail: "Displaying this document isn't supported here yet.",
  },
  "file-unavailable": {
    title: "This document can't be displayed",
    detail: "This document's file isn't available.",
  },
  "handshake-timeout": {
    title: "Connection not established",
    detail:
      "The embedding page didn't provide credentials. Reload the page to try again.",
  },
  // Deliberately neutral: cortex owns what a given status means (and its
  // codes can change), so the viewer lists the possibilities without
  // asserting one. `copyFor` refines this using standard HTTP semantics only
  // (the 5xx class, 429) — never a cortex-specific meaning for a code.
  "request-failed": {
    title: "This document can't be displayed",
    detail:
      "It may not exist, or the provided credentials may not have access to it.",
  },
  network: {
    title: "Something went wrong",
    detail: "The document couldn't be loaded. Reload the page to try again.",
  },
};

function copyFor({ kind, status }: ViewerError) {
  if (kind === "request-failed" && status !== undefined) {
    if (status >= 500) {
      return {
        title: "Something went wrong",
        detail:
          "Aiera couldn't process the request. Reload the page to try again.",
      };
    }
    if (status === 429) {
      return {
        title: "Too many requests",
        detail: "Wait a moment, then reload the page to try again.",
      };
    }
  }
  return COPY[kind];
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-viewer-bg px-6 text-center font-viewer text-viewer-fg">
      {children}
    </div>
  );
}

export function ErrorState({ kind, status }: ViewerError) {
  const { title, detail } = copyFor({ kind, status });
  // Every terminal failure renders through this component, making it the one
  // choke point that reports errors to the embedding host. Only settled
  // states render here (data fetches have exhausted their retries), though a
  // settled state can still change — a late credential recovers
  // handshake-timeout into a later `loaded` report, by design (see
  // handshake.ts).
  useEffect(() => notifyEmbedError({ kind, status }), [kind, status]);
  return (
    <Centered>
      <p
        className="m-0 text-base font-semibold"
        data-error-kind={kind}
        data-error-status={status}
      >
        {title}
      </p>
      <p className="m-0 text-sm text-viewer-muted">{detail}</p>
      {status !== undefined && (
        // The raw status, for support: readable off a screenshot, and the
        // same number the host receives.
        <p className="m-0 text-xs text-viewer-muted tabular-nums">
          HTTP {status}
        </p>
      )}
    </Centered>
  );
}

export function Loading() {
  return (
    <Centered>
      <span
        role="status"
        aria-label="Loading"
        className="size-5 animate-spin rounded-full border-2 border-viewer-border border-t-viewer-accent"
      />
    </Centered>
  );
}
